import { useEffect, useState } from 'react'

import Statuses from '@cinch/oxd-utils/request-status/src'
import { WbacService } from '@cinch/oxd-utils/requests/src'
import { get as getStorage, StorageKeys } from '@cinch/oxd-utils/storage/src'

interface Option {
  value: string
  label: string
}

const tidyOptions = (res: any) =>
  res.data.map(({ value, text: label }: { value: string; text: string }) => ({ value, label }))

const useDamage = () => {
  const { token: storageToken } = getStorage(StorageKeys.wbac)
  const serviceConfig = { headers: { Authorization: `JWT ${storageToken}` } }

  const fetchZones = async () => await WbacService.get('/damage/zones', serviceConfig)
  const fetchComponents = async (zoneId: string) =>
    await WbacService.get(`/damage/zones/${zoneId}/components`, serviceConfig)
  const fetchFaults = async (componentId: string) =>
    await WbacService.get(`/damage/components/${componentId}/faults`, serviceConfig)

  const [zones, setZones] = useState<Option[]>([])
  const [zoneStatus, setZoneStatus] = useState<string>(Statuses.INITIAL)

  const [components, setComponents] = useState<Option[]>([])
  const [componentStatus, setComponentStatus] = useState<string>(Statuses.INITIAL)

  const [faults, setFaults] = useState<Option[]>([])
  const [faultStatus, setFaultStatus] = useState<string>(Statuses.INITIAL)

  const getZones = () => {
    setZoneStatus(Statuses.PENDING)
    fetchZones()
      .then((res) => {
        setZones(tidyOptions(res))
        setZoneStatus(Statuses.SUCCESS)
      })
      .catch(() => {
        setZoneStatus(Statuses.ERROR)
      })
  }

  const getComponents = (zoneId: string) => {
    setComponentStatus(Statuses.PENDING)
    fetchComponents(zoneId)
      .then((res) => {
        setComponents(tidyOptions(res))
        setComponentStatus(Statuses.SUCCESS)
      })
      .catch(() => {
        setComponentStatus(Statuses.ERROR)
      })
  }

  const getFaults = (componentId: string) => {
    setFaultStatus(Statuses.PENDING)
    fetchFaults(componentId)
      .then((res) => {
        setFaults(tidyOptions(res))
        setFaultStatus(Statuses.SUCCESS)
      })
      .catch(() => {
        setFaultStatus(Statuses.ERROR)
      })
  }

  const resetComponents = () => {
    setComponentStatus(Statuses.INITIAL)
    setComponents([])
  }

  const resetFaults = () => {
    setFaultStatus(Statuses.INITIAL)
    setFaults([])
  }

  const resetAll = () => {
    resetComponents()
    resetFaults()
  }

  useEffect(() => {
    getZones()
  }, [])

  return {
    zones,
    zoneStatus,
    components,
    componentStatus,
    faults,
    faultStatus,
    getComponents,
    getFaults,
    getZones,
    resetComponents,
    resetFaults,
    resetAll,
  }
}

export default useDamage
